import { useContext } from "react";
import { Navigate, useParams } from "react-router-dom";
import { AuthContext } from "../context/auth";

/**
 * Niveles de usuario:
 * 1 = Administrador
 * 2 = Supervisor
 * 3 = Usuario
 */

/**
 * Tipos de rutas:
 * - 'global': Rutas públicas sin restricción (ej: TGD)
 * - 'public': Rutas públicas que redirigen a dashboard si el usuario está logueado
 * - 'private': Rutas privadas que requieren autenticación y pueden requerir permisos específicos
 */

const GuardRoute = ({ type, path, component }) => {
    const { usuario } = useContext(AuthContext);
    const { window } = useParams(); // Permite ir a una pantalla especial tras login por token

    // Estados del usuario:
    // - false: Aún no se ha leído del localStorage
    // - null: Fue leído y no hay usuario (no logueado)
    // - object: Usuario logueado con sus datos

    const kickLogin = <Navigate to="/login" replace />;
    const kickDashboard = <Navigate to="/dashboard" replace />;

    // Rutas globales: sin restricciones
    if (type === 'global') {
        return component;
    }

    // Rutas públicas: si el usuario está logueado, redirigir a dashboard
    if (type === 'public') {
        // Si el usuario está logueado, redirigir según parámetros o a dashboard
        if (usuario !== false && usuario !== null) {
            // Si hay un parámetro window, redirigir a página especial
            switch (window) {
                case "1": {
                    return <Navigate to="/dengue/nuevo" replace />;
                }
                default:
                    return kickDashboard;
            }
        }
        // Si no está logueado, permitir acceso
        return component;
    }

    // Rutas privadas: requieren autenticación y pueden requerir permisos
    if (type === 'private') {
        // Si el usuario no está logueado, redirigir a login
        if (usuario === null) {
            return kickLogin;
        }

        // Si aún no se ha cargado el usuario, esperar (mostrar componente vacío o loading)
        if (usuario === false) {
            return component; // O podrías retornar un componente de loading
        }

        // Usuario autenticado: verificar permisos según la ruta
        const permissionCheck = checkRoutePermissions(path, usuario);
        if (permissionCheck !== null) {
            return permissionCheck;
        }
    }

    // Por defecto, permitir acceso
    return component;
};

/**
 * Verifica si el usuario tiene permisos para acceder a una ruta específica
 * @param {string} path - Ruta a verificar
 * @param {object} usuario - Objeto usuario con nivel_usuario y permisos
 * @returns {JSX.Element|null} - Redirección a dashboard si no tiene permisos, null si tiene permisos
 */
const checkRoutePermissions = (path, usuario) => {
    const kickDashboard = <Navigate to="/dashboard" replace />;

    // Rutas de gestión de usuarios (/usuario, /usuario/nuevo, /usuario/editar)
    // Requiere: Administrador (nivel_usuario === 1) O permiso de supervisión de usuarios (sup_usuarios === true)
    if (path.includes("/usuario")) {
        const isAdmin = usuario.nivel_usuario === 1;
        const hasUserSupervision = usuario.sup_usuarios === true;
        if (!isAdmin && !hasUserSupervision) {
            return kickDashboard;
        }
    }

    // Rutas de gestión de áreas
    // Requiere: Administrador (nivel_usuario === 1) O permiso de supervisión de áreas (sup_areas === true)
    if (path.includes("/area")) {
        const isAdmin = usuario.nivel_usuario === 1;
        const hasAreaSupervision = usuario.sup_areas === true;
        if (!isAdmin && !hasAreaSupervision) {
            return kickDashboard;
        }
    }

    // Rutas que requieren ser Administrador exclusivamente
    // (puedes agregar más rutas aquí según necesites)
    const adminOnlyRoutes = [
        // Ejemplo: "/configuracion", "/reportes-avanzados"
        // Agregar rutas que requieren solo administrador
    ];
    
    for (const adminRoute of adminOnlyRoutes) {
        if (path.includes(adminRoute)) {
            if (usuario.nivel_usuario !== 1) {
                return kickDashboard;
            }
        }
    }

    // Si pasa todas las verificaciones, permitir acceso
    return null; // null indica que se debe renderizar el componente
};

export default GuardRoute;
